import React, { useMemo, useCallback, useState } from 'react';

import { makeStyles, Container, Grid, Paper } from '@material-ui/core';
import BreadCrumb from 'app/components/BreadCrumb';
import { injectIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { FormServiceContext } from 'app/utils/contexts';
import LicenseDuplicateRegistration from 'app/components/ServicesForms/Licenses/LicenseDuplicateRegistration';
import withRequestInitiation from 'app/components/Hoc/withRequestInitiation';
import { SERVICES_PROPERTIES } from 'app/config/services';

import styles from './styles';
import { generatePath, useHistory } from 'react-router-dom';
import { URLS } from 'app/config/urls';
import { errorHandler } from 'app/utils/errorHandler';
import { validateLicenseDuplicateRequest } from 'app/utils/forms/validators/license';
import { preparePayload } from 'app/utils/forms/functions';
import { hideLoading, showLoading } from 'app/store/slices/settings';
import { fetchSaveRequest, updateRequestDetails } from 'app/store/slices/requests';
import licenseEndpoint from 'app/api/services/licenseEndpoint';
import notification from 'app/config/notification';
import { STATUS_CODES } from 'app/config/status';
import alertsEndpoint from 'app/api/services/alertsEndpoint';
const useStyles = makeStyles(styles);

function LicensesDuplicate(props) {
  const classes = useStyles();
  const history = useHistory();
  const dispatch = useDispatch();

  const [errorsList, setErrorsList] = useState({});

  const {
    intl,
    loadingDocuments,
    setLoadingDocuments,
    canEdit,
    editing,
    resetAndGo,
    onCancel,
    validateForm,
    breadCrumb,
    rightInfo,
  } = props;

  const service = useSelector(state => state.services.details);
  const request = useSelector(state => state.requests.details);
  const documentsStore = useSelector(state => state.services.documents);
  const activeCompany = useSelector(state => state.company.activeCompany);
  const registrationUserInfo = useSelector(state => state.auth.registrationInfo);
  const userAdmin = useSelector(state => state.auth.userAdmin);

  if (Object.keys(service).length === 0) {
    history.push(generatePath(URLS.SERVICES));
  }

  const initialValues = useMemo(() => {
    if (editing) {
      const value = JSON.parse(request.jsonSolicitacao);
      if (value.nuMeses) value.nuMeses = String(value.nuMeses);
      value.frequencyTest = null;
      return value;
    }
    if (service.cdServico && SERVICES_PROPERTIES[service.cdServico].formInitialValues) {
      return SERVICES_PROPERTIES[service.cdServico].formInitialValues;
    }
    return {};
  }, [editing, request.jsonSolicitacao, service.cdServico]);

  const onSave = useCallback(
    async (values, fromSubmit, isInternalForm, oldData) => {
      if (!canEdit) {
        if (request.situacao && !request.situacao?.boEdicaoPortalExterno) return;
      }

      const valuesCopy = { ...values };

      if (fromSubmit) {
        const errors = validateLicenseDuplicateRequest({ values: valuesCopy, oldData, intl, isInternalForm });
        if (Object.keys(errors).length > 0) {
          setErrorsList(errors);
          return;
        } else {
          setErrorsList({});
        }
      }

      const documentsObject = {
        store: documentsStore,
        files: valuesCopy.documentos,
      };

      if (request?.situacao) valuesCopy.situacao = request.situacao;

      let dataToPayload = {
        cdServico: service.cdServico,
      };

      if (request && request.cdServico) {
        dataToPayload = { ...request };
      }

      const preparedPayload = preparePayload({
        values: valuesCopy,
        documents: documentsObject,
        params: dataToPayload,
        activeCompany,
        userAdmin,
        registrationUserInfo
      });

      dispatch(showLoading());
      let showMessage = false;
      let response = false;
      if (!fromSubmit) showMessage = true;
      try {
        response = await fetchSaveRequest({
          isFormData: true,
          preparedPayload,
          dispatch,
          intl,
          showMessage,
          cdService: service.cdServico
        });

        if (response?.status === 200 && response?.data) {
          await dispatch(updateRequestDetails(response.data));
        }
        
      } catch (error) {
        errorHandler(error.response?.status, error.response?.data?.message);
      }
      if (!fromSubmit) dispatch(hideLoading());
      return response;
    },
    [
      activeCompany,
      canEdit,
      dispatch,
      documentsStore,
      intl,
      registrationUserInfo,
      request,
      service.cdServico,
      userAdmin,
    ]
  );

  const onSubmit = useCallback(
    async (values, oldData) => {
      if (!canEdit) {
        if (request.situacao && !request.situacao?.boEdicaoPortalExterno) return;
      }

      const currentStatusCode = request.cdSituacao;

      //save before finish
      const responseSave = await onSave(values, true, false, oldData);

      if (responseSave && responseSave.status === 200) {
        try {
          const response = await licenseEndpoint.finishRequest(responseSave.data.cdSolicitacao);
          if (response.status === 200) {
            notification(
              intl.formatMessage({ id: 'MESSAGES.SERVICE.FORMS.SUCCESSFUL_FINISHED_REQUEST' }),
              { toastId: 'SUCCESSFUL_FINISHED_REQUEST', type: 'success' }
            );

            if (currentStatusCode === STATUS_CODES.AGUARDANDO_RESOLUCAO_PENDENCIAS) {
              await alertsEndpoint.deleteAlert({ cdSolicitacao: request.cdSolicitacao });
            }
          }
          resetAndGo();
        } catch (error) {
          errorHandler(error.response?.status, error.response?.data?.message);
        }
      }
      dispatch(hideLoading());
    },
    [
      canEdit,
      dispatch,
      intl,
      onSave,
      request.cdSituacao,
      request.cdSolicitacao,
      request.situacao,
      resetAndGo,
    ]
  );

  const contextProvider = useMemo(() => {
    return {
      loadingDocuments,
      setLoadingDocuments,
      validateForm,
      onSubmit,
      onSave,
      onCancel,
      initialValues,
      validationSchema: SERVICES_PROPERTIES[service.cdServico]
        ? SERVICES_PROPERTIES[service.cdServico].validationSchema
        : {},
      canEdit,
      errorsList,
      setErrorsList,
    };
  }, [
    loadingDocuments,
    setLoadingDocuments,
    validateForm,
    onSubmit,
    onSave,
    onCancel,
    initialValues,
    service.cdServico,
    canEdit,
    errorsList,
    setErrorsList,
  ]);

  return (
    <Container>
      <BreadCrumb breadCrumb={breadCrumb} rightInfo={rightInfo} />
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <Paper className={classes.container} elevation={3}>
            <FormServiceContext.Provider value={contextProvider}>
              <LicenseDuplicateRegistration {...props} />
            </FormServiceContext.Provider>
          </Paper>
        </Grid>
      </Grid>
    </Container>
  );
}

export default injectIntl(withRequestInitiation(LicensesDuplicate));
